var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import _ from 'lodash';
import { TweenMax } from 'gsap/all';
import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import { ActivityManagerTyped as ActivityManager } from 'mixins/ActivityManager';
import ActivityStage from '../components/activity/ActivityStage.vue';
import ActivityNextSectionPortal from '../components/activity/ActivityNextSectionPortal.vue';
import HtmlRenderer from '../components/activity/HtmlRenderer.vue';
import { ElementTypes } from '../utils/html-util';
import ActivityWrapper from '../components/activity/ActivityWrapper.vue';
import mixins from 'vue-typed-mixins';
const HINT = 'HINT';
const INCORRECT_ATTEMPT = 'INCORRECT_ATTEMPT';
export default mixins(AssetLoader, ActivityManager(), TypingActivityHelper, ErrorLogger).extend({
    name: 'SelectIt',
    components: {
        ActivityStage,
        HtmlRenderer,
        ActivityNextSectionPortal,
        ActivityWrapper
    },
    data() {
        return {
            wrongGuesses: [],
            correctGuesses: [],
            selected: [],
            focusedText: null,
            displayCorrectIcon: false,
            displayWrongIcon: false,
            showCorrectAnswers: false
        };
    },
    computed: {
        numOfCorrectAnswers() {
            return this.currentRound.answers.filter(answer => answer.correct).length;
        },
        hasGuessedAllAnswers() {
            return this.correctGuesses.length === this.numOfCorrectAnswers;
        }
    },
    watch: {
        correctGuesses() {
            if (this.hasGuessedAllAnswers) {
                this.handleActivityDone();
            }
        },
        focusedText(clickableAnswer) {
            if (!clickableAnswer)
                document.removeEventListener('keypress', this.addEnterListener);
            document.addEventListener('keypress', this.addEnterListener);
        }
    },
    methods: {
        getActivityState() {
            const { rounds } = this.activityData;
            return {
                rounds,
                incorrectAttempt: this.activityData.incorrectAttempt,
                instructions: Object.assign(Object.assign({}, rounds[0].instructions), { text: rounds[0].instructions.audio.description })
            };
        },
        loadActivity() {
            return __awaiter(this, void 0, void 0, function* () {
                const activityState = this.getActivityState();
                this.createRounds(activityState);
                const audiosMap = {
                    [HINT]: activityState.rounds[0].hint.audio.src,
                    [INCORRECT_ATTEMPT]: activityState.incorrectAttempt.audio.src
                };
                const audioDescriptionsMap = {
                    [HINT]: activityState.rounds[0].hint.audio.description,
                    [INCORRECT_ATTEMPT]: activityState.incorrectAttempt.audio.description
                };
                this.sfx = yield this.loadAAAudios(audiosMap);
                this.sfxDescriptions = audioDescriptionsMap;
            });
        },
        startActivity() {
            this.setRound();
            this.$nextTick(this.setActivityContainerHeight);
        },
        setRound() {
            this.setupNewRound();
            this.errorLoggerStart();
            this.$nextTick(() => {
                TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
            });
        },
        addEnterListener(e) {
            if (e.key === 'Enter') {
                this.handleOnClick(this.focusedText, true);
            }
        },
        getClickableAnswer(index) {
            return this.currentRound.answers.find(answer => index >= answer.startIndex && index <= answer.endIndex);
        },
        isGuessedCorrectly(clickableAnswer) {
            return this.correctGuesses.includes(clickableAnswer);
        },
        isGuessedIncorrectly(clickableAnswer) {
            return this.wrongGuesses.includes(clickableAnswer);
        },
        isSelected(clickableAnswer) {
            return this.selected.includes(clickableAnswer);
        },
        shouldShowHint(clickableAnswer) {
            return this.showCorrectAnswers && clickableAnswer.correct;
        },
        shouldShowEndOfActivity(clickableAnswer) {
            return this.isGuessedCorrectly(clickableAnswer) && this.hasGuessedAllAnswers;
        },
        getTextClass(index, element) {
            const clickableAnswer = this.getClickableAnswer(index);
            if (!clickableAnswer)
                return [''];
            const isEndingIndex = index === clickableAnswer.endIndex;
            let elPos = '';
            if (isEndingIndex && element.type === ElementTypes.TEXT)
                elPos = 'endingElement';
            const isFocusedOn = this.isFocusedOn(clickableAnswer) ? 'focused' : '';
            if (this.shouldShowEndOfActivity(clickableAnswer))
                return ['endActivityCorrect', elPos, isFocusedOn];
            if (this.isGuessedCorrectly(clickableAnswer))
                return ['correctHighlight', elPos];
            if (this.isGuessedIncorrectly(clickableAnswer))
                return ['wrongHighlight', elPos];
            if (this.isSelected(clickableAnswer))
                return ['selectedHighlight', elPos, isFocusedOn];
            if (this.shouldShowHint(clickableAnswer))
                return ['correctAnswerHint', elPos, isFocusedOn];
            if (clickableAnswer)
                return ['clickableHighlight', elPos, isFocusedOn];
            return [''];
        },
        handleCorrectAnswer(clickableAnswer) {
            this.correctGuesses = _.uniq([...this.correctGuesses, clickableAnswer]);
        },
        handleWrongAnswer(clickableAnswer) {
            return __awaiter(this, void 0, void 0, function* () {
                this.displayWrongIcon = true;
                if (clickableAnswer)
                    this.wrongGuesses = _.uniq([...this.wrongGuesses, clickableAnswer]);
                if (this.numberOfAttempts[0] <= 1)
                    return this.playAudio(INCORRECT_ATTEMPT);
            });
        },
        getCanBeSelected(clickableAnswer) {
            const alreadyGuessedCorrectly = this.correctGuesses.includes(clickableAnswer);
            if (this.hasGuessedAllAnswers || alreadyGuessedCorrectly)
                return;
            return true;
        },
        handleOnClick(clickableAnswer, isKeyboard = false) {
            this.focusedText = null;
            if (!this.getCanBeSelected(clickableAnswer))
                return;
            if (this.selected.includes(clickableAnswer)) {
                this.selected = [...this.selected].filter(selectedAnswer => clickableAnswer !== selectedAnswer);
                return;
            }
            const hasReachedMaxClicks = this.selected.length >= this.numOfCorrectAnswers - this.correctGuesses.length;
            if (hasReachedMaxClicks)
                return;
            this.focusedText = null;
            this.selected = _.uniq([...this.selected, clickableAnswer]);
        },
        getTabIndex(index, el) {
            const textClass = this.getTextClass(index, el);
            const isEndingEl = textClass.includes('endingElement');
            const isGuessedCorrectly = textClass.includes('correctHighlight');
            return isEndingEl && !isGuessedCorrectly ? '10' : '-1';
        },
        handleOnFocusIn(index, event) {
            const isMouseClick = !event.target.matches(':focus-visible');
            // prevents the focused color popping up before the selected color
            if (isMouseClick)
                return;
            const clickableAnswer = this.getClickableAnswer(index);
            if (!clickableAnswer || !this.getCanBeSelected(clickableAnswer))
                return;
            this.focusedText = clickableAnswer;
        },
        handleOnFocusOut() {
            this.focusedText = null;
            document.removeEventListener('keypress', this.addEnterListener);
        },
        isFocusedOn(clickableAnswer) {
            return this.focusedText === clickableAnswer;
        },
        maybeClickHandler(index) {
            const clickableAnswer = this.getClickableAnswer(index);
            if (!clickableAnswer)
                return;
            this.handleOnClick(clickableAnswer);
        },
        handleResetAfterSubmit() {
            if (this.numberOfAttempts[0] > 1 && !this.hasGuessedAllAnswers) {
                this.promptScreenReaderHintAlert();
                this.playAudio(HINT);
                this.showCorrectAnswers = true;
            }
            this.focusedText = null;
            this.selected = [];
            this.wrongGuesses = [];
            this.displayWrongIcon = false;
        },
        handleOnActivitySubmit() {
            if (this.hasGuessedAllAnswers)
                return;
            let audioPlaying = Promise.resolve();
            this.incrementNumberOfAttempts();
            if (!this.selected.length)
                audioPlaying = this.handleWrongAnswer();
            this.selected.forEach(selectedAnswer => {
                if (selectedAnswer.correct) {
                    this.handleCorrectAnswer(selectedAnswer);
                    return;
                }
                audioPlaying = this.handleWrongAnswer(selectedAnswer);
            });
            audioPlaying.finally(() => {
                TweenMax.delayedCall(1.5, this.handleResetAfterSubmit);
            });
        },
        handleActivityDone() {
            this.displayCorrectIcon = true;
            TweenMax.delayedCall(3, () => {
                this.processEndOfActivity();
            });
        },
        promptScreenReaderHintAlert() {
            this.$nextTick(() => {
                const answers = {};
                let currentAnswer = '';
                let currentAnswerIndex = 0;
                const charArr = Array.from(document.getElementsByClassName('correctAnswerHint') || []);
                if (charArr.length) {
                    charArr.forEach(node => {
                        currentAnswer = currentAnswer.concat(node.innerText);
                        if (node.classList.contains('endingElement')) {
                            answers[`${currentAnswerIndex}`] = currentAnswer;
                            currentAnswer = '';
                            currentAnswerIndex += 1;
                        }
                    });
                }
                if (answers['0']) {
                    const answersLength = Object.keys(answers).length;
                    const phraseOrPhrases = answersLength > 1 ? 'phrases, separated by commas, ' : 'phrase';
                    const isOrAre = answersLength > 1 ? 'are the following:' : 'is the following:';
                    this.setScreenReaderAlert(`The correct ${phraseOrPhrases} to be selected ${isOrAre} ${Object.keys(answers)
                        .map(key => `"${answers[key]}"`)
                        .join(', ')}`);
                }
            });
        }
    }
});
